import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Send, Mic, MicOff, Paperclip, X } from 'lucide-react';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, Tabs, Tab, Textarea } from '@heroui/react';
import { FileAttachment } from '@/services/apis/guide-ai-chat';
import FileUploadField from '../base/FileUploadField';
import { AcceptedFileType, FileUploadTabType } from '../base/file-upload/utils';
import { FileMetadata } from '@/services/apis/files';
import MediaLibraryContainer from '@/containers/media-library/MediaLibraryContainer';
import { useTranslation } from 'react-i18next';

interface ChatInputProps {
  onSend: (message: string, attachments?: FileAttachment[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

export interface ChatInputHandle {
  focus: () => void;
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  ({ onSend, disabled = false, placeholder = 'Type your message...' }, ref) => {
    const { t } = useTranslation('admin');
    const [input, setInput] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [attachments, setAttachments] = useState<FileAttachment[]>([]);
    const [isFileModalOpen, setIsFileModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState<string>('upload');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isSpeechSupported, setIsSpeechSupported] = useState(false);
    const recognitionRef = useRef<any>(null);
    const textareaRef = useRef<HTMLDivElement>(null);
    const inputBeforeRecordingRef = useRef<string>('');

    useImperativeHandle(ref, () => ({
      focus: () => {
        // HeroUI Textarea wraps the native textarea, so we need to find it
        const textarea = textareaRef.current?.querySelector('textarea') || textareaRef.current;
        textarea?.focus();
      },
    }));

    useEffect(() => {
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = 'en-US';

        recognitionRef.current.onresult = (event: any) => {
          let interimTranscript = '';
          let finalTranscript = '';

          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }

          // Update input with the base text + any final transcript + interim transcript
          const baseText = inputBeforeRecordingRef.current;
          const separator = baseText ? ' ' : '';

          if (finalTranscript) {
            // When we get final results, update the base text
            inputBeforeRecordingRef.current = baseText + separator + finalTranscript;
            setInput(inputBeforeRecordingRef.current + (interimTranscript ? ' ' + interimTranscript : ''));
          } else {
            // Show interim results in real-time
            setInput(baseText + separator + interimTranscript);
          }
        };

        recognitionRef.current.onerror = () => {
          setIsRecording(false);
        };

        recognitionRef.current.onend = () => {
          setIsRecording(false);
        };

        setIsSpeechSupported(true);
      }
    }, []);

    const handleSend = () => {
      if (!input.trim() && attachments.length === 0) return;
      if (disabled) return;

      onSend(input.trim(), attachments.length > 0 ? attachments : undefined);
      setInput('');
      setAttachments([]);
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };

    const toggleRecording = () => {
      if (!recognitionRef.current) {
        alert('Speech recognition is not supported in your browser');
        return;
      }

      if (isRecording) {
        recognitionRef.current.stop();
        setIsRecording(false);
      } else {
        // Store the current input before starting recording
        inputBeforeRecordingRef.current = input;
        recognitionRef.current.start();
        setIsRecording(true);
      }
    };

    const handleFileUploaded = (fileMetadata: FileMetadata) => {
      const attachment: FileAttachment = {
        id: fileMetadata.id,
        name: fileMetadata.name,
        url: fileMetadata.url,
        type: fileMetadata.type,
      };
      setAttachments((prev) => [...prev, attachment]);
      setIsFileModalOpen(false);
    };

    const handleFileSelect = (fileMetadata: FileMetadata) => {
      handleFileUploaded(fileMetadata);
    };

    const removeAttachment = (id: string) => {
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    };

    return (
      <>
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
          {/* Attached Files Display */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="flex items-center gap-2 px-3 py-2 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg"
                >
                  <span className="text-sm font-medium text-primary-700 dark:text-primary-300 truncate max-w-[200px]">
                    {attachment.name}
                  </span>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    className="min-w-0 w-5 h-5"
                    onPress={() => removeAttachment(attachment.id)}
                    aria-label="Remove attachment"
                  >
                    <X className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 items-center">
            <div className="flex gap-1">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={() => setIsFileModalOpen(true)}
                aria-label="Attach file"
              >
                <Paperclip className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1" ref={textareaRef}>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => handleKeyPress(e as React.KeyboardEvent<HTMLTextAreaElement>)}
                placeholder={placeholder}
                disabled={disabled}
                minRows={1}
                maxRows={4}
              />
            </div>

            <div className="flex gap-1">
              {isSpeechSupported && (
                <Button
                  isIconOnly
                  variant="light"
                  size="sm"
                  onPress={toggleRecording}
                  color={isRecording ? 'danger' : 'default'}
                  aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
                >
                  {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>
              )}

              <Button
                isIconOnly
                color="primary"
                size="sm"
                onPress={handleSend}
                disabled={disabled || (!input.trim() && attachments.length === 0)}
                aria-label="Send message"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* File Upload Modal */}
        <Modal
          isOpen={isFileModalOpen}
          onClose={() => setIsFileModalOpen(false)}
          size="5xl"
          placement="center"
          backdrop="blur"
          scrollBehavior="inside"
          classNames={{
            base: 'max-h-[90vh]',
            body: 'py-0',
          }}
        >
          <ModalContent>
            <ModalHeader className="flex flex-col gap-1">
              {t('guides.builder.modals.attachFile.title', 'Attach File')}
            </ModalHeader>
            <ModalBody>
              <Tabs
                selectedKey={selectedTab}
                onSelectionChange={(key) => setSelectedTab(key as string)}
                aria-label="File attachment options"
              >
                <Tab key="upload" title={t('guides.builder.modals.attachFile.tabs.upload', 'Upload File')}>
                  <div className="py-4">
                    <FileUploadField
                      file={uploadedFile}
                      onChange={setUploadedFile}
                      acceptedTypes={[AcceptedFileType.PDF, AcceptedFileType.IMAGE, AcceptedFileType.VIDEO]}
                      shownTabs={[FileUploadTabType.FILE_UPLOAD]}
                      onFileUploaded={handleFileUploaded}
                    />
                  </div>
                </Tab>
                <Tab key="library" title={t('guides.builder.modals.attachFile.tabs.library', 'Media Library')}>
                  <div className="py-4">
                    <MediaLibraryContainer
                      onFileSelect={handleFileSelect}
                      acceptedTypes={[AcceptedFileType.PDF, AcceptedFileType.IMAGE, AcceptedFileType.VIDEO]}
                    />
                  </div>
                </Tab>
              </Tabs>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  },
);

ChatInput.displayName = 'ChatInput';

export default ChatInput;
